import { useEffect, useState } from "react";
import { Bluetooth, Loader2, RefreshCw } from "lucide-react";
import { listQueued, syncAll, syncOne, removeVoucher, type QueuedVoucher } from "@/lib/ble/queue";
import { formatMoney } from "@/lib/money";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const PendingVouchers = () => {
  const [items, setItems] = useState<QueuedVoucher[]>([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setItems(await listQueued());
  };

  useEffect(() => {
    void load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const retry = async (v: QueuedVoucher) => {
    setBusy(true);
    try {
      const r = await syncOne(v);
      if (r.ok) toast.success("Voucher settled");
      else toast.error(r.error ?? "Still pending");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const retryAll = async () => {
    setBusy(true);
    try {
      await syncAll();
      await load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    await removeVoucher(id);
    await load();
  };

  if (items.length === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-card p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bluetooth className="h-4 w-4 text-primary" />
          <span className="font-display font-bold text-sm">Pending Bluetooth transfers</span>
        </div>
        <Button size="sm" variant="ghost" onClick={retryAll} disabled={busy}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Retry all
        </Button>
      </div>
      <ul className="space-y-2">
        {items.map((v) => (
          <li key={v.id} className="flex items-center gap-3 bg-secondary/50 rounded-xl px-3 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">
                {v.role === "sender" ? "Sent" : "Received"} {formatMoney(v.payload?.amount_cents ?? 0)}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {v.status}
                {v.last_error ? ` — ${v.last_error}` : ""}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => retry(v)} disabled={busy}>
              Retry
            </Button>
            <Button size="sm" variant="ghost" onClick={() => remove(v.id)}>
              ✕
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PendingVouchers;
